import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isWhiteLabel } from "../services/agencyService";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { getInitialQuestion, startConversation, sendMessage, getInterviewerTTSResponse, generateInsights } from "../services/interviewerService";
import { selectUserId } from "../store/slices/authUserSlice";
import { useSelector } from "react-redux";

import introAudioFile from "../assets/audio/potions_interviewer_intro_trimmed.mp3";

interface Message {
  text: string;
  sender: "user" | "assistant";
}

interface ConversationResponse {
  conversation_id: string;
  message: {
    response: string;
    followup: string;
  };
}

const Interviewer: React.FC = () => {
  const [showStartButton, setShowStartButton] = useState(true);
   // start with just the welcome message
  const [messages, setMessages] = useState<Message[]>([
    {
      text: isWhiteLabel() ? "Hi there! I'm your content interviewer. I'm here to help you generate content that'll resonate with your audience for your newsletter." : "Hi there! I'm the Potions interviewer. I'm here to help you generate content that'll resonate with your audience for your newsletter.",
      sender: "assistant",
    }
  ]);
  const [input, setInput] = useState("");
  const [topic, setTopic] = useState("");
  const [assistantQuestion, setAssistantQuestion] = useState<string>("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [showEmptyInsightsBanner, setShowEmptyInsightsBanner] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const userId = useSelector(selectUserId);
  const navigate = useNavigate();
  const location = useLocation();

  // set up speech recognition with proper hooks
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  // extract topic from URL parameters on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const topicParam = queryParams.get('topic');

    if (topicParam) {
      setTopic(topicParam);
    }
  }, [location]);

  // update input with transcript when it changes
  useEffect(() => {
    if (transcript) {
      setInput(transcript);

      if (showEmptyInsightsBanner) {
        setShowEmptyInsightsBanner(false);
      }
    }
  }, [transcript, showEmptyInsightsBanner]);

  useEffect(() => {
    setInput("");
  }, [])

  useEffect(() => {
    // auto-scroll to bottom when messages update
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // stop mic when component unmounts
    return () => {
      if (listening) {
        SpeechRecognition.stopListening();
      }
    };
  }, [listening, browserSupportsSpeechRecognition]);


  const handleStartInterview = () => {
    console.log("Starting interview...");

    setIsLoading(true);
    setShowStartButton(false);

    // fetch and display the initial question
    getInitialQuestion(topic).then((question) => {
      console.log("Initial question:", question);
      setAssistantQuestion(question);
      setTimeout(() => {
        setMessages(prev => [...prev, { text: question, sender: "assistant" }]);
        setIsLoading(false);
      }, 600);
    }).catch(error => {
      console.error("Error fetching initial question:", error);
      setIsLoading(false);
    });

    // const introAudio = new Audio(introAudioFile);
    // introAudio.play();
  };

  const handleQuickTopicSelect = (selectedTopic: string) => {
    setTopic(selectedTopic);
  };

  const handleGenerateInsights = async () => {
    if (!conversationId || !userId) return;

    try {
      if (listening) {
        SpeechRecognition.stopListening();
      }

      setIsGeneratingInsights(true);

      // generate insights from the conversation
      const insightsData = await generateInsights(userId, conversationId);
      console.log("Generated insights:", insightsData);

      // banner if no insights
      if (!insightsData || insightsData.length === 0) {
        setShowEmptyInsightsBanner(true);
        setIsGeneratingInsights(false);
        return;
      }

      // Navigate to the insights page
      navigate("/insights");

    } catch (error) {
      console.error("Error generating insights:", error);
      setIsGeneratingInsights(false);
    }
  };

  const playTTS = async (stream: ReadableStream<Uint8Array> | null) => {
    console.log("Playing TTS");
    if (!stream) return;

    console.log("Stream isn't null");

    const mediaSource = new MediaSource();
    const audio = new Audio();
    audio.src = URL.createObjectURL(mediaSource);
    audio.play(); // triggers autoplay immediately

    mediaSource.addEventListener("sourceopen", async () => {
      console.log("Media source is open");

      const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
      const reader = stream.getReader();

      const append = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log("Stream is done");
            mediaSource.endOfStream();
            break;
          };

          try {
            console.log("Appending buffer");
            await new Promise<void>((resolve) => {
              sourceBuffer.addEventListener("updateend", () => {
                console.log("Source buffer updated");
                resolve();
              }, { once: true });
              sourceBuffer.appendBuffer(value);
            });

          } catch (err) {
            console.log("Error appending buffer:", err);
            break;
          }
        }
      }

      await append();
    });
  };

  const handleSendMessage = async () => {
    if (input.trim() === "" || isLoading) return;

    const currentInput = input.trim();
    console.log("Sending message:", currentInput);

    // add user message
    const userMessage: Message = { text: currentInput, sender: "user" };
    setMessages(prev => [...prev, userMessage]);

    setInput("");
    resetTranscript();

    setShowEmptyInsightsBanner(false);
    setIsLoading(true);

    try {
      let responseData: ConversationResponse;

      if (!conversationId) {
        // create conversation on the user's first message
        responseData = await startConversation(userId, assistantQuestion, currentInput);
        setConversationId(responseData.conversation_id);
        console.log("Response data from start conversation:", responseData);
      } else {
        // subsequent messages
        responseData = await sendMessage(userId, conversationId, currentInput);
        console.log("Response data from send message:", responseData);
      }

      // add response and follow up messages
      setMessages(prev => [...prev, {
        text: responseData.message.response,
        sender: "assistant"
      }]);
      setTimeout(() => {
        setMessages(prev => [...prev, {
          text: responseData.message.followup,
          sender: "assistant"
        }]);
      }, 600);

      // TODO(jay): uncomment once the responses are shorter to play audio
      // getInterviewerTTSResponse(message).then(async (audioStream) => {
      //   console.log("Received audio stream - playing...");
      //   await playTTS(audioStream);
      // });
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, {
        text: "Sorry, there was an error processing your message. Please try again.",
        sender: "assistant"
      }]);
    } finally {
      setIsLoading(false);
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // toggle voice recognition on/off
  const toggleListening = () => {
    if (listening) {
      SpeechRecognition.stopListening();
    } else {
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
    }
  };

  // input change for banner
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    if (showEmptyInsightsBanner) {
      setShowEmptyInsightsBanner(false);
    }
  };

  return (
      <div className="interviewer-container">
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}>
          <h1>Studio</h1>
          <button
            onClick={() => navigate("/insights")}
            className="tertiary-info-button"
            style={{ cursor: "pointer" }}
          >
            View Insights
          </button>
        </div>
        <p style={{ marginTop: "0" }}>
          Create content by chatting with AI.
        </p>

        {showEmptyInsightsBanner && (
            <div className="empty-insights-banner">
              Please continue the conversation to generate insights.
            </div>
        )}

        <div className="chat-container">
          {showStartButton ? (
              <div className="start-section">
                <div className="topic-input-container">
                  <input
                      type="text"
                      className="topic-input"
                      placeholder={isWhiteLabel() ? "Type in a topic, or hit enter to have us run the interview topics" : "Type in a topic, or hit enter to have Potions run the interview topics"}
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleStartInterview();
                        }
                      }}
                  />
                  <button
                      className="start-arrow-button"
                      onClick={handleStartInterview}
                      aria-label="Start interview"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 4L16 10L10 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                            strokeLinejoin="round"/>
                      <path d="M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                            strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>

                <div className="quick-topics">
                  <button
                      className="quick-topic-button"
                      onClick={() => handleQuickTopicSelect("Events")}
                  >
                    Events
                  </button>
                  <button
                      className="quick-topic-button"
                      onClick={() => handleQuickTopicSelect("Industry News")}
                  >
                    Industry News
                  </button>
                  <button
                      className="quick-topic-button"
                      onClick={() => handleQuickTopicSelect("Product Updates")}
                  >
                    Product Updates
                  </button>
                </div>
              </div>
          ) : (
              <div className="message-container">
                {messages.map((message, index) => (
                    <div
                        key={index}
                        className={`message ${message.sender === 'user' ? 'user-message' : 'assistant-message'}`}
                    >
                      <div className="message-bubble">
                        {message.text}
                      </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                )}

                <div ref={messagesEndRef}/>
              </div>
          )}

          {!showStartButton && (
              <div className="input-container">
                <button
                    className={`voice-button ${listening ? 'voice-active' : ''}`}
                    onClick={toggleListening}
                    title={listening ? "Stop listening" : "Voice note"}
                    disabled={isLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                  </svg>
                </button>
                <textarea
                    className="message-input"
                    placeholder="Reply..."
                    rows={3}
                    value={input}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                />
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px"
                }}>
                  <button
                      className="primary-button"
                      style={{marginTop: "0px"}}
                      onClick={handleSendMessage}
                      disabled={isLoading || input.trim() === ""}
                  >
                    Send
                  </button>

                  {conversationId && (
                      <button
                          className="tertiary-info-button"
                          style={{cursor: "pointer"}}
                          onClick={handleGenerateInsights}
                          disabled={isGeneratingInsights}
                      >
                        Finish
                      </button>
                  )}
                </div>
              </div>
          )}
        </div>
      </div>
  );
};

export default Interviewer;
